import { useCallback } from 'react';
import { useAnalytics } from '../../analytics/useAnalytics';
import { ANALYTICS_EVENTS } from '../../analytics/events';
import { isMatchWinnerSlip } from '../types/create.types';
import type { PickSlip, CreateStep } from '../types/create.types';

interface UseTrackingPickCallbacksProps {
  step: CreateStep;
  pickSlipsLength: number;
  handleRemovePick: (gameId: string) => void;
  handleRemoveSlip: (slip: PickSlip) => void;
  handleDeleteMarkets: (gameId: string, marketIds: number[]) => void;
}

interface UseTrackingPickCallbacksReturn {
  handleRemovePickWithTracking: (gameId: string) => void;
  handleRemoveSlipWithTracking: (slip: PickSlip) => void;
  handleDeleteMarketsWithTracking: (gameId: string, marketIds: number[]) => void;
}

export const useTrackingPickCallbacks = ({
  step,
  pickSlipsLength,
  handleRemovePick,
  handleRemoveSlip,
  handleDeleteMarkets,
}: UseTrackingPickCallbacksProps): UseTrackingPickCallbacksReturn => {
  const { track } = useAnalytics();

  const handleRemovePickWithTracking = useCallback(
    (gameId: string) => {
      track(ANALYTICS_EVENTS.CREATE_CHALLENGE_PICK_REMOVED, {
        step,
        game_id: gameId,
        picks_count: pickSlipsLength,
        slip_type: 'match_winner',
      });
      handleRemovePick(gameId);
    },
    [track, step, handleRemovePick, pickSlipsLength]
  );

  const handleRemoveSlipWithTracking = useCallback(
    (slip: PickSlip) => {
      const slipType = isMatchWinnerSlip(slip) ? 'match_winner' : 'other';
      track(ANALYTICS_EVENTS.CREATE_CHALLENGE_PICK_REMOVED, {
        step,
        game_id: slip.gameId,
        picks_count: pickSlipsLength,
        slip_type: slipType,
      });
      handleRemoveSlip(slip);
    },
    [track, step, handleRemoveSlip, pickSlipsLength]
  );

  const handleDeleteMarketsWithTracking = useCallback(
    (gameId: string, marketIds: number[]) => {
      if (marketIds.length === 0) return;
      track(ANALYTICS_EVENTS.CREATE_CHALLENGE_PICK_REMOVED, {
        step,
        game_id: gameId,
        picks_count: pickSlipsLength,
        slip_type: 'other',
      });
      handleDeleteMarkets(gameId, marketIds);
    },
    [track, step, handleDeleteMarkets, pickSlipsLength]
  );

  return {
    handleRemovePickWithTracking,
    handleRemoveSlipWithTracking,
    handleDeleteMarketsWithTracking,
  };
};
